// pages/tasks/DataAnalystTatPicker.jsx
//
// "TAT Level" chooser for a Data Analyst Own Ticket (create + edit), shown as
// four selectable cards in one row (two on phones):
//
//   [ High    ] [ Medium  ] [ Low     ] [ New Requirement ]
//   [ 4 Hours ] [ 2 Days  ] [ 5 Days  ] [ Custom TAT      ]
//
// Each card is a real radio input (keyboard + screen-reader friendly).
// High/Medium/Low show the fixed Applied TAT and have NO value/unit inputs;
// only New Requirement shows Value + Unit. The Applied TAT panel is always
// visible so the result of a choice is seen immediately. The backend derives
// the fixed values itself (services/dataAnalystTatService.js) -- this is
// display only.
import React from "react";
import { Form } from "react-bootstrap";
import {
  NEW_REQUIREMENT,
  TAT_LEVEL_OPTIONS,
  TAT_UNIT_OPTIONS,
  appliedTat,
  formatTat,
  levelHint,
  levelLabel,
  tatErrors,
} from "./dataAnalystTat";

// `current` (edit only): the ticket's saved TAT { level, value, unit }. While
// the same level stays selected, saving does NOT change the TAT (the backend
// treats it as "no change"), so the Applied panel shows the saved value --
// e.g. an older "Medium · 10 Days" -- instead of the level's default.
export default function DataAnalystTatPicker({ idPrefix, value, onChange, showErrors = false, legend = "TAT Level", current = null }) {
  const { level = "", value: tatValue = "", unit = "" } = value || {};
  const errors = tatErrors({ level, value: tatValue, unit });
  const keepsCurrent = !!current && !!current.level && level === current.level
    && (level !== NEW_REQUIREMENT || (String(tatValue).trim() === String(current.value) && unit === current.unit));
  const applied = keepsCurrent ? current : appliedTat({ level, value: tatValue, unit });
  const set = (patch) => onChange?.({ level, value: tatValue, unit, ...patch });

  return (
    <fieldset className="own-ticket-tat-section" aria-describedby={`${idPrefix}-help`}>
      <legend className="own-ticket-tat-title">
        {legend} <span className="text-danger">*</span>
      </legend>

      <div className="own-ticket-tat-levels" role="radiogroup" aria-label={legend}>
        {TAT_LEVEL_OPTIONS.map((o) => (
          <Form.Check
            key={o.value}
            type="radio"
            id={`${idPrefix}-level-${o.value}`}
            name={`${idPrefix}-level`}
            className={`own-ticket-tat-level-choice${level === o.value ? " is-selected" : ""}`}
            checked={level === o.value}
            onChange={() => set({ level: o.value })}
            label={
              <span className="own-ticket-tat-level-label">
                <span className="own-ticket-tat-level-name">{o.label}</span>
                <span className="own-ticket-tat-level-hint">{levelHint(o.value)}</span>
              </span>
            }
          />
        ))}
      </div>
      {showErrors && errors.level && <div className="text-danger small mt-1">{errors.level}</div>}

      <div className="own-ticket-tat-result">
        {level === NEW_REQUIREMENT && (
          <div className="own-ticket-tat-custom">
            <div className="own-ticket-tat-custom-title">Custom TAT</div>
            <div className="own-ticket-tat-grid">
              <Form.Group controlId={`${idPrefix}-value`}>
                <Form.Label>Value <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  placeholder="e.g. 7"
                  value={tatValue}
                  isInvalid={showErrors && !!errors.value}
                  onChange={(e) => set({ value: e.target.value })}
                />
                <Form.Control.Feedback type="invalid">{errors.value}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId={`${idPrefix}-unit`}>
                <Form.Label>Unit <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={unit}
                  isInvalid={showErrors && !!errors.unit}
                  onChange={(e) => set({ unit: e.target.value })}
                >
                  <option value="">Hours / Days</option>
                  {TAT_UNIT_OPTIONS.map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.unit}</Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
        )}

        {applied ? (
          <div className="own-ticket-tat-applied" aria-live="polite" data-testid={`${idPrefix}-applied`}>
            <div className="own-ticket-tat-applied-title">Applied TAT</div>
            <div className="own-ticket-tat-applied-level">{levelLabel(applied.level)}</div>
            <div className="own-ticket-tat-applied-value">{formatTat(applied.value, applied.unit)}</div>
            {keepsCurrent && <div className="own-ticket-tat-applied-note">Current TAT · unchanged</div>}
          </div>
        ) : (
          <div className="own-ticket-tat-applied is-empty" aria-live="polite">
            <div className="own-ticket-tat-applied-title">Applied TAT</div>
            <div className="own-ticket-tat-applied-value">
              {level === NEW_REQUIREMENT ? "Enter a value and unit" : "Select a level"}
            </div>
          </div>
        )}
      </div>

      <Form.Text id={`${idPrefix}-help`} className="text-muted d-block mt-2">
        High, Medium and Low use a fixed TAT. Choose New Requirement to set a custom TAT.
      </Form.Text>
    </fieldset>
  );
}
